import React, { useState, useEffect, useRef } from "react";
import moment from "moment";

import CloseSVG from "../../../../images/icons/svg/close.svg";
import LeftArrowSVG from "../../../../images/icons/svg/left-arrow.svg";
import TrashSVG from "../../../../images/icons/svg/trash-grey.svg";
import chatService from "../../../../services/Chat";
import httpClient from "../../../../lib/HttpClient";
import ChatMessage, { ChatMessageLoading } from "./ChatMessage";

import styles from "./Chats.module.scss";

const Chats = (props) => {
  const {
    docId,
    libraryId,
    closePanel,
    onCitationClick,
    currentSessionId,
    onSessionChange,
    paigeStatus: initialPaigeStatus,
    onPaigeStatusChange,
  } = props;

  // Determine context type
  const isLibraryContext = !!libraryId;
  const contextId = isLibraryContext ? libraryId : docId;

  // Local state for paige status (allows polling updates)
  // For library context, always treat as completed (no processing needed)
  const [paigeStatus, setPaigeStatus] = useState(
    isLibraryContext ? "completed" : initialPaigeStatus
  );

  // State
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(currentSessionId);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Load sessions on mount
  useEffect(() => {
    loadSessions();
  }, [contextId]);

  // Track if we're in the middle of sending (to prevent reload race condition)
  const isSendingRef = useRef(false);

  // Load messages when active session changes
  useEffect(() => {
    if (activeSessionId) {
      // Don't reload messages if we're in the middle of sending
      // (we already have optimistic messages displayed)
      if (!isSendingRef.current) {
        loadSessionMessages(activeSessionId);
      }
      // Notify parent of session change for persistence
      if (onSessionChange) {
        onSessionChange(activeSessionId);
      }
    } else {
      setMessages([]);
    }
  }, [activeSessionId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages, isSending]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        120
      )}px`;
    }
  }, [inputValue]);

  // Update local state when prop changes (document context only)
  useEffect(() => {
    if (!isLibraryContext) {
      setPaigeStatus(initialPaigeStatus);
    }
  }, [initialPaigeStatus, isLibraryContext]);

  // Poll for paige_status updates when pending or processing (document context only)
  useEffect(() => {
    // Skip polling for library context
    if (isLibraryContext) {
      return;
    }
    if (paigeStatus !== "pending" && paigeStatus !== "processing") {
      return;
    }
    const pollInterval = setInterval(() => {
      httpClient
        .get("/documents/" + docId + ".json")
        .then(function (response) {
          const newStatus = response.data.paige_status;
          if (newStatus !== paigeStatus) {
            setPaigeStatus(newStatus);
            // Notify parent so it can update its state too
            if (onPaigeStatusChange) {
              onPaigeStatusChange(newStatus);
            }
            // If now completed, load sessions
            if (newStatus === "completed") {
              loadSessions();
            }
          }
        })
        .catch(function (error) {
          console.error("Error polling paige status:", error);
        });
    }, 5000); // Poll every 5 seconds

    return function () {
      clearInterval(pollInterval);
    };
  }, [paigeStatus, docId, isLibraryContext]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const loadSessions = async (preserveActiveSession = false) => {
    setSessionsLoading(true);
    try {
      const response = isLibraryContext
        ? await chatService.getSessionsForLibrary(libraryId)
        : await chatService.getSessionsForDocument(docId);
      const sessionsList = response.data || [];
      setSessions(sessionsList);

      // If preserveActiveSession is true, don't change the active session
      // (used when refreshing after sending a message)
      if (preserveActiveSession) {
        return;
      }

      // If we have a currentSessionId from props, use it
      // Otherwise if there are sessions and no active one, select the most recent
      if (currentSessionId) {
        setActiveSessionId(currentSessionId);
      } else if (sessionsList.length > 0 && !activeSessionId) {
        setActiveSessionId(sessionsList[0].id);
      }
    } catch (error) {
      console.error("Error loading chat sessions:", error);
    } finally {
      setSessionsLoading(false);
    }
  };

  const loadSessionMessages = async (sessionId) => {
    setIsLoading(true);
    try {
      const response = await chatService.getSession(sessionId);
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error("Error loading session messages:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewChat = () => {
    // Clear active session - will create new one on first message
    setActiveSessionId(null);
    setMessages([]);
    setInputValue("");
  };

  const handleSessionSelect = (sessionId) => {
    setActiveSessionId(sessionId);
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = async () => {
    const query = inputValue.trim();
    if (!query || isSending) return;

    setIsSending(true);
    isSendingRef.current = true;
    setInputValue("");

    try {
      let sessionId = activeSessionId;

      // Create session if this is the first message
      if (!sessionId) {
        const createResponse = await chatService.createSession();
        sessionId = createResponse.data.id;
        setActiveSessionId(sessionId);
      }

      // Optimistically add user message
      const tempUserMessage = {
        id: `temp-${Date.now()}`,
        role: "user",
        content: query,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, tempUserMessage]);

      // Send message to API with appropriate context
      const context = isLibraryContext
        ? [{ type: "Library", id: parseInt(libraryId) }]
        : [{ type: "Document", id: parseInt(docId) }];
      const response = await chatService.sendMessage(sessionId, query, context);

      // Replace temp message with real ones and add assistant response
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== tempUserMessage.id);
        return [
          ...filtered,
          response.data.user_message,
          response.data.assistant_message,
        ];
      });

      // Refresh sessions list to get updated title/timestamp
      // Pass true to preserve the current active session
      loadSessions(true);
    } catch (error) {
      console.error("Error sending message:", error);
      // Remove optimistic message on error
      setMessages((prev) =>
        prev.filter((m) => !String(m.id).startsWith("temp-"))
      );
    } finally {
      setIsSending(false);
      isSendingRef.current = false;
    }
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Check if chat is available based on paige_status
  const isChatAvailable = paigeStatus === "completed";

  const getStatusMessage = () => {
    switch (paigeStatus) {
      case "pending":
      case "processing":
        return {
          title: "Processing document",
          text: "This document is being prepared for Paige. This may take a few minutes.",
          showSpinner: true,
        };
      case "failed":
        return {
          title: "Processing failed",
          text: "This document could not be processed for Paige. Please try re-uploading the document.",
          showSpinner: false,
        };
      case "not_applicable":
        return {
          title: "Not available",
          text: "Paige is not available for this file type.",
          showSpinner: false,
        };
      default:
        return null;
    }
  };

  const handleDeleteSession = async () => {
    if (!activeSessionId) return;

    const confirmed = window.confirm(
      "Are you sure you want to delete this conversation? This cannot be undone."
    );
    if (!confirmed) return;

    try {
      await chatService.deleteSession(activeSessionId);

      // Remove from local state
      const remainingSessions = sessions.filter(
        (s) => s.id !== activeSessionId
      );
      setSessions(remainingSessions);

      // Select next session or clear if none left
      if (remainingSessions.length > 0) {
        setActiveSessionId(remainingSessions[0].id);
      } else {
        setActiveSessionId(null);
        setMessages([]);
      }
    } catch (error) {
      console.error("Error deleting session:", error);
    }
  };

  return (
    <div className={styles.container}>
      {/* Sessions Sidebar */}
      <div
        className={`${styles.sessionsSidebar} ${
          sidebarCollapsed ? styles.collapsed : ""
        }`}
      >
        <div className={styles.sidebarHeader}>
          <span className={styles.sidebarTitle}>Recents</span>
          <button className={styles.newChatButton} onClick={handleNewChat}>
            + New
          </button>
        </div>
        <div className={styles.sessionsList}>
          {sessionsLoading ? (
            <div className={styles.noSessions}>Loading...</div>
          ) : sessions.length === 0 ? (
            <div className={styles.noSessions}>
              No conversations yet. Start by asking a question!
            </div>
          ) : (
            sessions.map((session) => (
              <div
                key={session.id}
                className={`${styles.sessionItem} ${
                  activeSessionId === session.id ? styles.active : ""
                }`}
                onClick={() => handleSessionSelect(session.id)}
              >
                <div className={styles.sessionTitle}>
                  {session.title || "New conversation"}
                </div>
                <div className={styles.sessionDate}>
                  {moment(session.updated_at).fromNow()}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className={styles.chatMain}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button
              className={styles.toggleSidebarButton}
              onClick={toggleSidebar}
              title={
                sidebarCollapsed ? "Show conversations" : "Hide conversations"
              }
            >
              <img
                src={LeftArrowSVG}
                style={{
                  transform: sidebarCollapsed ? "rotate(180deg)" : "rotate(0)",
                  transition: "transform 0.2s",
                }}
                alt=""
              />
            </button>
            <div className={styles.title}>Ask Paige</div>
          </div>
          <div className={styles.headerRight}>
            {activeSessionId && (
              <div
                className={styles.deleteButton}
                onClick={handleDeleteSession}
                title="Delete conversation"
              >
                <img src={TrashSVG} alt="Delete" />
              </div>
            )}
            <div className={styles.closeButton} onClick={closePanel}>
              <img src={CloseSVG} alt="Close" />
            </div>
          </div>
        </div>

        <div className={styles.body}>
          {!isChatAvailable ? (
            (() => {
              const statusMessage = getStatusMessage();
              return (
                <div className={styles.statusState}>
                  {statusMessage && statusMessage.showSpinner && (
                    <div className={styles.statusSpinner}>
                      <ChatMessageLoading />
                    </div>
                  )}
                  <div className={styles.statusTitle}>
                    {statusMessage && statusMessage.title}
                  </div>
                  <div className={styles.statusText}>
                    {statusMessage && statusMessage.text}
                  </div>
                </div>
              );
            })()
          ) : isLoading ? (
            <div className={styles.loadingState}>
              <ChatMessageLoading />
            </div>
          ) : messages.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyStateTitle}>
                {isLibraryContext
                  ? "Ask Paige about this library"
                  : "Ask Paige about this document"}
              </div>
              <div className={styles.emptyStateText}>
                {isLibraryContext
                  ? "I can help you find information across all documents in this library and provide citations."
                  : "I can help you find information, answer questions, and provide citations from the document."}
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => (
                <ChatMessage
                  key={message.id}
                  message={message}
                  onCitationClick={onCitationClick}
                  isLibraryContext={isLibraryContext}
                />
              ))}
              {isSending && <ChatMessageLoading />}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {isChatAvailable && (
          <div className={styles.inputContainer}>
            <div className={styles.inputWrapper}>
              <div className={styles.textareaWrapper}>
                <textarea
                  ref={textareaRef}
                  className={styles.textarea}
                  value={inputValue}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    isLibraryContext
                      ? "Ask a question about this library..."
                      : "Ask a question about this document..."
                  }
                  disabled={isSending}
                  rows={1}
                />
              </div>
              <button
                className={styles.sendButton}
                onClick={handleSend}
                disabled={!inputValue.trim() || isSending}
              >
                {isSending ? "..." : "Send"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chats;
